import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Alert, Switch, FormControlLabel, Collapse,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { AdmissionApplication, AdmissionCycle, ApplicationStatus } from '../../../types/admissions';
import StatusChip, { statusLabel } from '../../common/StatusChip';
import SearchableSelect from '../../common/SearchableSelect';
import { feeGateFromError, FeeGateError } from '../../../utils/admissionFees';
import { decisionRuleFromError, DecisionRuleBlock, eligibilityFailures, planFor } from '../../../utils/admissionAssessment';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useUpcomingEmails } from '../../email/useUpcomingEmails';
import UpcomingEmailNotice from '../../email/UpcomingEmailNotice';
import { DECISION_EMAIL, decisionEmailRequests } from '../decisionEmails';

/**
 * Generic transition dialog. Mirrors the backend's state machine
 * (services/admissions/applicationStateMachine.ts) so the dropdown only
 * shows valid targets from the current state. The reach-anywhere Withdrawn
 * transition is deliberately NOT offered here — WithdrawDialog handles it
 * because it needs a reason + eventual refund flow.
 */

interface Props {
  open: boolean;
  application: AdmissionApplication;
  /** The application's cycle — its stage settings decide which moves exist. */
  cycle: AdmissionCycle;
  onClose: () => void;
  onDone: () => Promise<void> | void;
  /** Limit the choices to these stages (e.g. a pipeline column a card was
   *  dropped on). With exactly one, it is pre-selected. */
  targets?: ApplicationStatus[];
}

/** Mirror of backend ALLOWED_TRANSITIONS (minus the reach-anywhere Withdrawn
 *  rule — that goes through WithdrawDialog). Kept in sync by hand. */
export const APPLICATION_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  Draft:                 ['Submitted'],
  Submitted:             ['AppFeePending', 'UnderReview', 'Ineligible'],
  AppFeePending:         ['UnderReview', 'OfferLapsed'],
  UnderReview:           ['DocsRequired', 'Ineligible', 'AssessmentScheduled', 'Offered', 'Waitlisted', 'Rejected'],
  DocsRequired:          ['UnderReview'],
  Ineligible:            ['UnderReview', 'AppFeePending'],
  AssessmentScheduled:   ['Assessed', 'Absent'],
  Absent:                ['AssessmentScheduled', 'Rejected'],
  Assessed:              ['Offered', 'Waitlisted', 'Rejected'],
  Offered:               ['OfferAccepted', 'OfferDeclined', 'OfferLapsed'],
  OfferAccepted:         ['ConfirmedConditional', 'AdmFeePending', 'Confirmed'],
  OfferDeclined:         [],
  OfferLapsed:           [],
  Waitlisted:            ['Offered', 'NotAdmitted'],
  NotAdmitted:           ['Waitlisted'],
  Rejected:              ['UnderReview', 'Absent', 'Assessed'],
  ConfirmedConditional:  ['AdmFeePending', 'Confirmed'],
  AdmFeePending:         ['Confirmed', 'OfferLapsed'],
  Confirmed:             ['Enrolled'],
  Enrolled:              [],
  Withdrawn:             [],
};

/** What each move means, shown under the option so staff pick the right
 *  step rather than guess from the status name. */
const MOVE_HINT: Partial<Record<ApplicationStatus, string>> = {
  Submitted:            'Application received and complete',
  AppFeePending:        'Waiting for the application fee',
  UnderReview:          'Ready for the admissions team to review',
  DocsRequired:         'Ask the family for missing documents',
  Ineligible:           'Does not meet the eligibility rules',
  AssessmentScheduled:  'Test or interview has been scheduled',
  Assessed:             'Assessment marks are recorded',
  Absent:               'Did not attend the assessment',
  Offered:              'Offer the applicant a seat',
  Waitlisted:           'Hold on the waitlist for a seat',
  Rejected:             'Not selected for admission',
  OfferAccepted:        'Family has accepted the offer',
  OfferDeclined:        'Family has declined the offer',
  OfferLapsed:          'Deadline passed without a response',
  ConfirmedConditional: 'Confirmed, subject to open conditions',
  AdmFeePending:        'Waiting for the admission fee',
  Confirmed:            'Seat confirmed — ready for handover',
  Enrolled:             'Handed over as an enrolled student',
  NotAdmitted:          'Waitlist closed without a seat',
};

/** Dialog title when it is opened for one specific move. */
const MOVE_TITLE: Partial<Record<ApplicationStatus, string>> = {
  Offered: 'Offer a seat',
  Waitlisted: 'Add to the waitlist',
  Rejected: 'Reject application',
  AssessmentScheduled: 'Schedule assessment',
  Absent: 'Mark absent',
  NotAdmitted: 'Close as not admitted',
  OfferLapsed: 'Mark offer lapsed',
};

/** Moves a school must give a reason for. */
const REASON_REQUIRED: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>(['Rejected', 'NotAdmitted', 'Ineligible']);

/** Closed outcomes that can be reopened — always with a reason. Mirror of
 *  the backend's REOPENABLE_STATUSES. */
const REOPENABLE: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>(['Ineligible', 'Rejected', 'NotAdmitted']);

/** A decision reopens only to the stage it was made at — read from the
 *  timeline. Mirror of the backend's `reopenOrigin`; `undefined` when the
 *  status doesn't reopen to its origin, `null` when the origin isn't on record. */
const reopenOrigin = (app: Pick<AdmissionApplication, 'status' | 'timeline'>): ApplicationStatus | null | undefined => {
  if (app.status !== 'Rejected' && app.status !== 'NotAdmitted') return undefined;
  const closing = [...(app.timeline || [])].reverse().find(e => e.to === app.status);
  return closing?.from ?? null;
};

/** Moves that happen through their own flow, never by picking a status:
 *  Assessed (when the last mark is saved), OfferAccepted (Record acceptance,
 *  which captures the terms), OfferLapsed (automatic at the deadline),
 *  Enrolled (Enrollment handover). */
const FLOW_ONLY: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>(['Assessed', 'OfferAccepted', 'OfferLapsed', 'Enrolled']);

/** Stages an application can be moved to by hand under this cycle's
 *  settings (Withdraw is separate). Mirrors the backend selection gates. */
/** Mirror of backend DECISION_STATUSES: moves that also need the
 *  `admissions_decisions` permission. Kept in sync by hand. */
export const DECISION_STATUSES: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'Offered', 'Waitlisted', 'Rejected', 'NotAdmitted', 'OfferLapsed',
]);

/** The moves staff can make from the application's status. `canDecide` is
 *  whether the user holds `admissions_decisions`; without it, decision moves
 *  — including reversing one — are left out. */
export const nextStatuses = (app: Pick<AdmissionApplication, 'status' | 'timeline' | 'classId'>, cycle: AdmissionCycle, canDecide: boolean): ApplicationStatus[] => {
  const from = app.status;
  const plan = planFor(cycle, app);
  const origin = reopenOrigin(app);
  if (origin !== undefined && !canDecide) return [];
  return (APPLICATION_TRANSITIONS[from] || []).filter(to => {
    if (origin !== undefined) return to === origin; // back to where the decision was made
    if (FLOW_ONLY.has(to)) return false;
    // Awaiting fee only exists when the cycle charges an application fee.
    if (to === 'AppFeePending' && !(cycle.applicationFeeAmount > 0)) return false;
    if (!canDecide && DECISION_STATUSES.has(to)) return false;
    if (!plan.hasAssessment && (to === 'AssessmentScheduled' || to === 'Absent')) return false;
    if (plan.hasAssessment && (from === 'UnderReview' || from === 'DocsRequired') && (to === 'Offered' || to === 'Waitlisted')) return false;
    return true;
  });
};

const TransitionDialog: React.FC<Props> = ({ open, application, cycle, onClose, onDone, targets }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const canDecide = canWrite('admissions_decisions');
  const options = useMemo(
    () => nextStatuses(application, cycle, canDecide).filter(s => !targets || targets.includes(s)),
    [application, cycle, canDecide, targets],
  );
  const [target, setTarget] = useState<ApplicationStatus | ''>(() => (options.length === 1 ? options[0] : ''));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Set when the server refuses the move because a fee is unpaid.
  const [feeGate, setFeeGate] = useState<{ gate: FeeGateError; message: string } | null>(null);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  // Set when an offer breaks merit order / the pass mark.
  const [ruleBlock, setRuleBlock] = useState<DecisionRuleBlock | null>(null);
  const [rationale, setRationale] = useState('');
  // Failing eligibility rules the school may waive (needs admissions_decisions).
  const failures = useMemo(() => eligibilityFailures(application), [application]);
  const [waive, setWaive] = useState(false);
  // An offer or rejection may email the family — say so, and to whom, before it's applied.
  const emailRequests = useMemo(
    () => (target && DECISION_EMAIL[target] ? decisionEmailRequests([{ to: target, id: application._id }]) : []),
    [target, application._id],
  );
  const upcoming = useUpcomingEmails(emailRequests);

  const resetFeeGate = () => {
    setFeeGate(null); setOverride(false); setOverrideReason('');
    setRuleBlock(null); setRationale('');
  };

  const requestApproval = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.requestDecisionOverride(application._id, rationale.trim());
      enqueueSnackbar('Sent to the principal for approval', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not send the request', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setTarget('');
    setReason('');
    setWaive(false);
    resetFeeGate();
    onClose();
  };

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await admissionApplicationService.transition(application._id, target, {
        reason: reason.trim() || undefined,
        feeOverride: override ? { reason: overrideReason.trim() } : undefined,
        waiveEligibility: waive || undefined,
      });
      enqueueSnackbar(`Status changed to ${statusLabel({ kind: 'application', value: target })}`, { variant: 'success' });
      await onDone();
    } catch (err: any) {
      const gate = feeGateFromError(err);
      const rule = decisionRuleFromError(err);
      if (gate) setFeeGate({ gate, message: err.message });
      else if (rule) setRuleBlock(rule);
      else enqueueSnackbar(err?.message || 'Failed to change status', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const noOptions = options.length === 0;
  const overrideIncomplete = override && !overrideReason.trim();
  // Reopening a closed application, or waiving the rules, is explained on the record too.
  const reasonRequired = !!target && (REASON_REQUIRED.has(target) || REOPENABLE.has(application.status) || waive);
  // Offered as soon as the applicant is moving on (not being closed).
  const canWaive = canDecide && failures.length > 0 && !!target && !REASON_REQUIRED.has(target);

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>
          {(targets?.length === 1 && MOVE_TITLE[targets[0]]) || 'Change status'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <StatusChip status={{ kind: 'application', value: application.status }} />
            <ArrowForward fontSize="small" sx={{ color: 'text.disabled' }} />
            {target
              ? <StatusChip status={{ kind: 'application', value: target }} />
              : <Typography variant="body2" color="text.secondary">Choose the next stage</Typography>}
          </Stack>
          {noOptions && REOPENABLE.has(application.status) && !canDecide ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                Reopening a decision is itself a decision — ask someone who makes admission decisions.
              </Typography>
            </Alert>
          ) : noOptions ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                This application has reached a final stage, so its status can&apos;t move further.
                Use <b>Withdraw application</b> from the actions menu if it needs to be cancelled.
              </Typography>
            </Alert>
          ) : (
            <>
              <SearchableSelect
                label="Change to"
                value={target}
                onChange={(v) => { setTarget(v as ApplicationStatus | ''); resetFeeGate(); }}
                options={options.map(s => ({
                  value: s,
                  label: statusLabel({ kind: 'application', value: s }),
                  secondary: MOVE_HINT[s],
                }))}
                placeholder="Select a stage"
                fullWidth
                minWidth={0}
              />
              <TextField
                label={reasonRequired ? 'Reason' : 'Reason (optional)'}
                required={reasonRequired}
                placeholder="E.g. parent confirmed on call; receipt submitted"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                size="small"
                fullWidth
                helperText={upcoming.groups.length ? 'Recorded on the application timeline. It isn’t in the email.' : 'Recorded on the application timeline for audit.'}
              />
              <UpcomingEmailNotice upcoming={upcoming} when="as soon as you apply" />
              {canWaive && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" fontWeight={600}>Doesn&apos;t meet the eligibility rules</Typography>
                  {failures.map(f => (
                    <Typography key={f} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{f}</Typography>
                  ))}
                  <FormControlLabel
                    sx={{ mt: 1, ml: 0 }}
                    control={<Switch size="small" checked={waive} onChange={(e) => setWaive(e.target.checked)} />}
                    label={<Typography variant="body2" color="text.primary">Waive the rules for this applicant</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    An exception the school grants. Saved with your name and the reason above; the rules stay on the record as waived.
                  </Typography>
                </Alert>
              )}
              {feeGate && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" fontWeight={600}>Fee not settled</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{feeGate.message}</Typography>
                  {feeGate.gate.canOverride ? (
                    <>
                      <FormControlLabel
                        sx={{ mt: 1, ml: 0 }}
                        control={<Switch size="small" checked={override} onChange={(e) => setOverride(e.target.checked)} />}
                        label={<Typography variant="body2" color="text.primary">Move anyway and record an override</Typography>}
                      />
                      <Collapse in={override} unmountOnExit>
                        <TextField
                          label="Why is the fee being skipped?"
                          placeholder="E.g. Principal approved deferred payment until 15 March"
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          required
                          multiline
                          minRows={2}
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          helperText="Saved on the timeline with your name. The challan stays open for collection."
                        />
                      </Collapse>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      Record the payment from the Fees tab — the application moves on by itself once it is paid.
                    </Typography>
                  )}
                </Alert>
              )}
              {ruleBlock && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" fontWeight={600}>Needs the principal's approval</Typography>
                  {ruleBlock.reasons.map(r => (
                    <Typography key={r} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{r}</Typography>
                  ))}
                  {ruleBlock.canRequestOverride ? (
                    <TextField
                      label="Reason for the exception"
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                      required
                      multiline
                      minRows={2}
                      size="small"
                      fullWidth
                      sx={{ mt: 1.5 }}
                      helperText="An approver sees this with the reasons above. The seat is offered when they approve."
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      Ask someone who makes admission decisions to request an exception.
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        {ruleBlock ? (
          <Button
            variant="contained"
            disableElevation
            onClick={requestApproval}
            disabled={busy || !ruleBlock.canRequestOverride || !rationale.trim()}
          >
            {busy ? 'Sending…' : 'Request approval'}
          </Button>
        ) : (
          <Button
            variant="contained"
            disableElevation
            onClick={submit}
            disabled={busy || upcoming.loading || noOptions || !target || (!!feeGate && !override) || overrideIncomplete || (reasonRequired && !reason.trim())}
          >
            {busy ? 'Applying…' : override ? 'Override and apply' : upcoming.sending && target ? `${DECISION_EMAIL[target]!.verb} and send email` : 'Apply change'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransitionDialog;
